class HelperService {
  debounce<T extends (...args: unknown[]) => Promise<void>>(func: T, wait: number, waitForAsync: boolean = true) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isRunning = false;
    let latestArgs: Parameters<T> | null = null;

    const runFunc = async () => {
      if (!latestArgs) return;
      isRunning = true;
      const args = latestArgs;
      latestArgs = null;

      try{
        const promise = func(...args);
        if (waitForAsync) {
          await promise; // wait for async function to complete
        }
      } finally {
        isRunning = false;
      }

      if (latestArgs) {
        runFunc(); // run again if there were new calls while running
      }
    };

    return function (...args: Parameters<T>) {
      latestArgs = args;

      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (!isRunning) {
          runFunc();
        }
        // else runFunc will be triggered after current run finishes
      }, wait);
    };
  }

  public throttle<T extends (...args: unknown[]) => Promise<void>>(func: T, wait: number) {
    let lastCallTime = 0;
    let isRunning = false;
    let latestArgs: Parameters<T> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const runFunc = async () => {
      if (!latestArgs) return;
      isRunning = true;
      const args = latestArgs;
      latestArgs = null;
      lastCallTime = Date.now();
      
      try {
        await func(...args);
      } finally {
        isRunning = false;
      }
      
      // If there were new calls while running, schedule the next execution
      if (latestArgs) {
        const timeSinceLastCall = Date.now() - lastCallTime;
        const remainingWait = Math.max(0, wait - timeSinceLastCall);
        
        timeoutId = setTimeout(() => {
          runFunc();
        }, remainingWait);
      }
    };

    return function (...args: Parameters<T>) {
      latestArgs = args;
      
      if (isRunning) {
        // Function is already running, just update args and let it handle scheduling
        return;
      }
      
      const timeSinceLastCall = Date.now() - lastCallTime;
      const remainingWait = Math.max(0, wait - timeSinceLastCall);
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (remainingWait === 0) {
        // Enough time has passed, run immediately
        runFunc();
      } else {
        // Schedule execution after remaining wait time
        timeoutId = setTimeout(() => {
          runFunc();
        }, remainingWait);
      }
    };
  }
  
  closeIncompleteJson<T>(input: string): T | null {
    const jsonStart = input.indexOf('{');
    if (jsonStart === -1) return null;

    let jsonFragment = input.slice(jsonStart);
    let stack: string[] = [];
    let inString = false;
    let escaped = false;

    for (let i = 0; i < jsonFragment.length; i++) {
      const char = jsonFragment[i];

      if (escaped) {
        escaped = false;
        continue;
      }

      if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = !inString;
      } else if (!inString) {
        if (char === '{') stack.push('}');
        else if (char === '[') stack.push(']');
        else if (char === '}' || char === ']') {
          if (stack.length && stack[stack.length - 1] === char) {
            stack.pop();
          } else {
            return null; // unbalanced
          }
        }
      }
    }

    if (inString) jsonFragment += '"';
    jsonFragment += stack.reverse().join('');
    try {
      return JSON.parse(jsonFragment) as T;
    } catch {
      return null;
    }
  }
  
}

export const helperService = new HelperService();